import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { findGroupById } from '@sonifi/wb-group/services/group.service';
import { setSelectedGroup } from '../store/groupsSlice';
import useOutsideClick from '@sonifi/wb-widget/hooks/useOutsideClick';

export const buildGroupLabel = (group) => {
  const cmsId = group.cms_id ? `${group.cms_id}, ` : '';
  const city = group.city ? ` (${group.city}` : '';
  const state = group.state ? `, ${group.state})` : `${city ? ')' : ''}`;

  return `${cmsId}${group.name}${city}${state}`;
};

const buildDefaultValue = (defaultValue, groups) => {
  if (!defaultValue) {
    return '';
  }
  const group = groups.find((g) => g.id === parseInt(defaultValue));
  return group ? buildGroupLabel(group) : '';
};

const GroupSelector = ({ customClass, defaultValue, groupKey, onChange }) => {
  const dispatch = useDispatch();
  const groups = useSelector((state) => state.groups.groups[groupKey]);
  const selectRef = useRef(null);
  const [groupFilter, setGroupFilter] = useState(
    buildDefaultValue(defaultValue, groups),
  );
  const [isFiltering, setIsFiltering] = useState(false);

  const openCloseSelect = (open) => {
    selectRef.current.style.display = open ? 'block' : 'none';
    selectRef.current.open = open;
  };

  useOutsideClick(selectRef, () => {
    selectRef.current.open && openCloseSelect(false);
  });

  const findAndSetGroup = useCallback(
    (groupId) => {
      const group = dispatch(findGroupById(groupId));
      dispatch(setSelectedGroup(group));
      return group;
    },
    [dispatch],
  );

  const handleSelectedGroup = useCallback(
    (groupId) => {
      const group = findAndSetGroup(groupId);
      openCloseSelect(false);
      setGroupFilter(buildGroupLabel(group));
      setIsFiltering(false);
      return group;
    },
    [findAndSetGroup],
  );

  const handleChange = (groupId) => {
    const group = handleSelectedGroup(groupId);
    onChange(group);
  };

  const onFiltering = (val) => {
    setIsFiltering(true);
    setGroupFilter(val);
    openCloseSelect(true);
  };

  const filterGroups = (group) => {
    if (isFiltering) {
      return (
        group.cms_id
          ?.toString()
          .toLowerCase()
          .includes(groupFilter.toLowerCase()) ||
        group.name?.toLowerCase().includes(groupFilter.toLowerCase()) ||
        group.city?.toLowerCase().includes(groupFilter.toLowerCase()) ||
        group.state?.toLowerCase().includes(groupFilter.toLowerCase())
      );
    }

    return true;
  };

  return (
    <div className={`group-selector-container ${customClass}`}>
      <input
        className="site-selector"
        onClick={() => openCloseSelect(!selectRef.current.open)}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onFiltering(e.target.value)}
        placeholder="Select Site"
        value={groupFilter}
        type="text"
      />
      <ul
        className="typeahead dropdown-menu am-fade"
        ref={selectRef}
        role="combobox"
        style={{ display: 'none' }}
      >
        {groups?.filter(filterGroups).map((group, i) => (
          <li
            key={`${group.cms_id}${group.id}${i}`}
            onClick={() => {
              handleChange(group.id);
            }}
            data-test={group.id}
          >
            {buildGroupLabel(group)}
          </li>
        ))}
      </ul>
    </div>
  );
};

GroupSelector.defaultProps = {
  customClass: '',
  onChange: () => {},
};

export default GroupSelector;
